import { useEffect, useRef } from 'react'

// useMagnetic — when the cursor is within `radius` px of the element's center,
// translate the element toward the cursor (max `maxOffset` px). Smooth follow
// during pull, springy snap-back when cursor leaves via CSS transition.
// Touch / reduced-motion: no-op.
export default function useMagnetic({ radius = 100, maxOffset = 6 } = {}) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof window === 'undefined') return undefined
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (coarse || reduced) return undefined

    // CSS spring snap-back — kept on the element while we're not in fast-pull
    // mode. cubic-bezier overshoots slightly for a magnetic feel.
    const prevTransition = el.style.transition
    el.style.transition = 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1)'

    let active = false

    const onMove = (e) => {
      const rect = el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      const dx = e.clientX - cx
      const dy = e.clientY - cy
      const dist = Math.hypot(dx, dy)
      if (dist > radius) {
        if (active) {
          active = false
          el.style.transform = 'translate3d(0, 0, 0)'
        }
        return
      }
      active = true
      const pull = (1 - dist / radius) * maxOffset
      const angle = Math.atan2(dy, dx)
      el.style.transform = `translate3d(${Math.cos(angle) * pull}px, ${Math.sin(angle) * pull}px, 0)`
    }

    const onDocLeave = () => {
      if (!active) return
      active = false
      el.style.transform = 'translate3d(0, 0, 0)'
    }

    window.addEventListener('mousemove', onMove, { passive: true })
    document.addEventListener('mouseleave', onDocLeave)

    return () => {
      window.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseleave', onDocLeave)
      el.style.transition = prevTransition
      el.style.transform = ''
    }
  }, [radius, maxOffset])

  return ref
}
